<script setup lang="ts">
import useEventStore from "~/stores/event";
import useEventLock from "~/hooks/events/use-event-lock";
import formatDate from "~/utils/format-date";
import { EVENT_STATUS } from "~/constants";

export interface EventTabsProps {
  event: IModelEvent | null;
}
const { t, locale } = useI18n();
const props = defineProps<EventTabsProps>();
const route = useRoute();
const appConfig = useRuntimeConfig().public;

const eventStore = useEventStore();
const isRealKickoffTimeEnabled = appConfig.APP_CUSTOMER.enable_real_kickoff_time;
const isTagsAvailable = appConfig.APP_CUSTOMER.web_sdk.enable_tagging_mode;
const isEnableMatchStats = appConfig.APP_CUSTOMER.enable_match_stats;

const commerceCategory = computed(() => props.event?.commerceCategory || "commerce_free_media");
const isEventAvailableForUser = computed(() => eventStore.data?.isEventAvailableForUser);
const isEventAvailableInUserRegion = computed(() => eventStore.data?.isEventAvailableInUserRegion);

const { lockMessage, isLocked, isGeoBlocked, isAuthLock } = useEventLock({
  commerceOptions: commerceCategory,
  isEventAvailableForUser,
  isEventAvailableInUserRegion,
});
const onEditTag = (tagId: string) => {
  if (eventStore.tags.data.has(tagId))
    eventStore.tags.activeEditTag = eventStore.tags.data.get(tagId) || null;
  eventStore.tags.isCreatingTag = true;
};

const videoId = computed(() => route.params.eventId as string || undefined);
const isCreatingClip = computed(() => eventStore.isCreatingClip);
const isCreatingTag = computed(() => eventStore.tags.isCreatingTag);
const isEditingTag = computed(() => eventStore.tags.isEditingTag);
const hasMediaView = computed(() => Boolean(route.query.mediaId));
const hasModalOpen = computed(() => isCreatingTag.value || isCreatingClip.value || isEditingTag.value);
const hasTabs = computed(() => isTagsAvailable && !hasMediaView.value && !hasModalOpen.value);
const eventGameStats = computed(() => eventStore.gameStats);
const eventScoreStats = computed(() => eventStore.scoreStats);
const hasStats = computed(() => props.event?.geniusId && !eventStore.hasGeniusError);
const hasStatisticsTab = computed(() => hasStats.value || ((eventScoreStats.value || eventGameStats.value) && isEnableMatchStats));
const isExternal = computed(() => eventStore.data?.isExternal);
const eventDate = computed(() => props.event?.gameStartDate);
const isUpcoming = computed(() => props.event?.eventStatus === EVENT_STATUS.UPCOMING);
const formattedEventDate = computed(() => formatDate(eventDate.value, "PPPp", locale.value as TAvailableLocale));

const activeTabIndex = ref(hasStatisticsTab.value ? "2" : "0");

const countDownText = computed(() => {
  return isRealKickoffTimeEnabled ? t("pages.events.time_till_kick_off") : t("pages.events.event_start_date", { date: formattedEventDate.value });
});

const showCountdown = ref(isRealKickoffTimeEnabled && (Date.now() < Number(eventDate.value)));

const onFinishCountdown = () => {
  if (isRealKickoffTimeEnabled) {
    showCountdown.value = false;
    return;
  }
  eventStore.isEventStartingSoon = true;
};

const onTimeCountdownUpdate = () => {
  if (!isRealKickoffTimeEnabled)
    return;

  if (isUpcoming.value && Date.now() > Number(props.event?.date))
    eventStore.isEventStartingSoon = true;
};

watch(hasModalOpen, () => {
  if (Date.now() < Number(eventDate.value))
    showCountdown.value = !showCountdown.value;
});
</script>

<template>
  <div class="relative mt-6 lg:mt-0">
    <div v-if="isUpcoming || showCountdown" class="h-full px-4 py-8 overflow-auto background-main rounded-main text-main xl:px-9">
      <div class="flex items-center justify-center mb-8">
        <IconCalendar fill="var(--base-icon-color)" class="mx-1" :height="16" :width="16" />
        <span class="mx-1 text-body-medium">
          {{ countDownText }}</span>
      </div>
      <EventCountDown :date="eventDate" @timeupdate="onTimeCountdownUpdate" @finish="onFinishCountdown" />
    </div>

    <BaseTabs v-else-if="hasTabs" v-model:active-index="activeTabIndex">
      <BaseTab :heading="t('labels.game_videos')">
        <div class="background-main rounded-main text-main overflow-auto h-[calc(100%-76px)] w-full lg:aspect-[1]">
          <div v-if="isLocked" class="flex flex-col items-center justify-center px-6 py-12 text-main">
            <IconLock fill="var(--base-icon-color)" width="24" height="32" />

            <p v-if="!isAuthLock && isGeoBlocked" class="my-6 text-center text-heading-3 text-secondary">
              {{ t('labels.lock_event_message.event_restricted') }}
            </p>
            <p v-else class="my-6 text-center text-heading-3 text-secondary">
              {{ lockMessage }}
            </p>
          </div>

          <EventVideos v-else />
        </div>
      </BaseTab>
      <BaseTab :heading="t('labels.tag', 2)">
        <div class="background-main rounded-main text-main overflow-auto h-[calc(100%-76px)] w-full lg:aspect-[1]">
          <div v-if="isLocked" class="flex flex-col items-center justify-center px-6 py-12 text-main">
            <IconLock fill="var(--base-icon-color)" width="24" height="32" />

            <p v-if="!isAuthLock && isGeoBlocked" class="my-6 text-center text-heading-3 text-secondary">
              {{ t('labels.lock_event_message.event_restricted') }}
            </p>
            <p v-else class="my-6 text-center text-heading-3 text-secondary">
              {{ lockMessage }}
            </p>
          </div>
          <div v-else-if="isExternal" class="flex flex-col items-center justify-center px-6 py-12 text-main">
            <IconLock fill="var(--base-icon-color)" width="24" height="32" />
            <p class="py-6 text-center text-body-medium text-secondary">
              {{ t('pages.events.tabs.event_tags.is_external_content') }}
            </p>
          </div>

          <EventTags v-else @edit="onEditTag" />
        </div>
      </BaseTab>
      <BaseTab v-if="hasStatisticsTab" :heading="t('labels.stats')" :icon="{ name: 'stats', path: `/events/stats/${event?.id}` }">
        <div class="background-main rounded-main text-main overflow-auto h-[calc(100%-76px)] w-full lg:aspect-[1]">
          <EventStats :event="event" :score-stats="eventScoreStats" :game-stats="eventGameStats" />
        </div>
      </BaseTab>
    </BaseTabs>

    <EventTagsForm v-else-if="isCreatingTag || isEditingTag" :media-item="event" />
    <EventVideosCreateClipModal v-else-if="isCreatingClip" :media-item="event" />

    <div v-else-if="hasMediaView" class="h-full">
      <div
        class="px-4 py-3 mb-6"
        style="background: var(--base-card-bgColor); border-radius: var(--base-border-radius);"
      >
        <BaseButton
          class="flex items-center"
          tag="nuxt-link"
          type="icon"
          :name="t('labels.watch_full_game')"
          :to="{ path: `/events/${videoId}/` }"
        >
          <IconChevronLeft :width="12" :height="12" class="mr-2 text-main" />
          <div class="text-lg font-medium text-main">
            {{ t("labels.watch_full_game") }}
          </div>
        </BaseButton>
      </div>

      <div class="background-main rounded-main text-main overflow-auto h-[calc(100%-76px)]">
        <EventVideos />
      </div>
    </div>
  </div>
</template>
